import type { CaseAgenticMissionLink, MissionStageProgress } from "@/services/api/case-checking";
import type { TaskStep } from "@/services/api/data-hub/types";

const STAGE_STATUS_TO_STEP: Record<string, TaskStep["status"]> = {
  completed: "done",
  complete: "done",
  active: "running",
  running: "running",
  pending: "pending",
};

export function missionStepPrefix(missionLink?: CaseAgenticMissionLink | null): string | null {
  const order = missionLink?.current_stage_order;
  const count = missionLink?.stage_count;
  if (order != null && count != null && count > 0) {
    return `Step ${order}/${count}`;
  }
  return null;
}

export function buildMissionStepLabel(prefix: string | null, subLabel: string): string {
  const trimmed = subLabel.trim();
  if (!trimmed) return prefix ?? "Starting…";
  if (prefix) return `${prefix} - ${trimmed}`;
  return trimmed;
}

/** Strip mission step prefix and legacy "In progress" wrappers. */
export function normalizeProgressSubLabel(label: string): string {
  const trimmed = label.trim();
  if (!trimmed) return "";
  const stepMatch = trimmed.match(/^Step \d+\/\d+ - (.+)$/);
  if (stepMatch) return stepMatch[1];
  if (trimmed.startsWith("In progress - ")) return trimmed.slice("In progress - ".length);
  if (trimmed.startsWith("In progress · ")) return trimmed.slice("In progress · ".length);
  return trimmed;
}

/** Default case-check progress label with optional mission step prefix. */
export function buildCaseProgressFallback(
  missionLink?: CaseAgenticMissionLink | null,
  subLabel = "Starting…",
): string {
  return buildMissionStepLabel(missionStepPrefix(missionLink), subLabel);
}

export function missionStagesToTaskSteps(
  stages: MissionStageProgress[] | null | undefined,
  currentStage?: string | null,
): TaskStep[] {
  if (!stages?.length) return [];
  return stages.map((stage) => {
    const rawStatus = (stage.status || "pending").toLowerCase();
    let status = STAGE_STATUS_TO_STEP[rawStatus] ?? "pending";
    if (currentStage && stage.name === currentStage && status === "pending") {
      status = "running";
    }
    return {
      label: stage.display_name || stage.name,
      status,
      detail: "",
      kind: "step",
    };
  });
}
